import { supabase, supabaseUrl } from '../lib/supabase'

// Types for summary generation
export type GenerationScope = 'all' | 'school' | 'student'

// Roles the summary pipeline generates for. Mirrors SUMMARY_TARGET_ROLES in
// supabase/functions/student-summary-cron/index.ts — keep the two in sync, or the
// picker below offers users the batch job skips (or hides ones it would summarize).
export const SUMMARY_TARGET_ROLES = ['student', 'teacher', 'admin', 'super_admin']

export interface GenerationOptions {
  scope: GenerationScope
  school_id?: string
  user_id?: string
  force?: boolean
}

export interface GenerationResult {
  success: boolean
  batch_id?: string
  job_id?: string
  student_count?: number
  requests_created?: number
  message?: string
  error?: string
  filtering_stats?: {
    total_students_with_activity: number
    skipped_already_summarized: number
    skipped_no_new_activity: number
    eligible_students: number
  }
}

export interface BatchJob {
  id: string
  openai_batch_id: string | null
  job_type: string
  status: 'pending' | 'validating' | 'in_progress' | 'finalizing' | 'completed' | 'failed' | 'expired' | 'cancelled'
  total_requests: number
  completed_requests: number | null
  failed_requests: number | null
  metadata: {
    summary_date?: string
    lookback_days?: number
    student_count?: number
    conversations_per_student?: number
    school_id?: string
    user_id?: string
    // Users routed to a portfolio-only update instead of a summary (no conversation in
    // the lookback window). Absent on rows written before this was recorded.
    portfolio_only_count?: number
    // Set only when the run produced no summaries at all: the job stays in_progress
    // until student-summary-poll sees this portfolio batch finish.
    portfolio_job_id?: string
  } | null
  error_message: string | null
  submitted_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
}

export class StudentSummaryGenerationService {
  /**
   * Trigger summary generation with specified scope
   */
  static async triggerGeneration(options: GenerationOptions): Promise<GenerationResult> {
    try {
      // Get the current session for authorization
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        return {
          success: false,
          error: 'Not authenticated'
        }
      }

      // Build request body based on scope
      const requestBody: Record<string, unknown> = {}

      if (options.force) {
        requestBody.force = true
      }

      switch (options.scope) {
        case 'school':
          if (!options.school_id) {
            return {
              success: false,
              error: 'school_id is required for school scope'
            }
          }
          requestBody.school_id = options.school_id
          break
        case 'student':
          if (!options.user_id) {
            return {
              success: false,
              error: 'user_id is required for student scope'
            }
          }
          requestBody.user_id = options.user_id
          break
        case 'all':
          // No additional parameters needed
          break
      }

      // Call the edge function
      const response = await fetch(`${supabaseUrl}/functions/v1/student-summary-cron`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        return {
          success: false,
          error: `API error: ${response.status} - ${errorText}`
        }
      }

      const result = await response.json()
      return result as GenerationResult

    } catch (error) {
      console.error('Error triggering summary generation:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get recent batch jobs for display
   */
  static async getRecentBatchJobs(limit: number = 10): Promise<BatchJob[]> {
    try {
      const { data, error } = await supabase
        .from('openai_batch_jobs')
        .select('*')
        .eq('job_type', 'daily_student_summary')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching batch jobs:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching batch jobs:', error)
      return []
    }
  }

  /**
   * Get a specific batch job by ID
   */
  static async getBatchJob(jobId: string): Promise<BatchJob | null> {
    try {
      const { data, error } = await supabase
        .from('openai_batch_jobs')
        .select('*')
        .eq('id', jobId)
        .single()

      if (error) {
        console.error('Error fetching batch job:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error fetching batch job:', error)
      return null
    }
  }

  /**
   * Check if there's an active job currently running
   */
  static async hasActiveJob(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('openai_batch_jobs')
        .select('id')
        .eq('job_type', 'daily_student_summary')
        .in('status', ['pending', 'validating', 'in_progress', 'finalizing'])
        .limit(1)

      if (error) {
        console.error('Error checking active jobs:', error)
        return false
      }

      return (data?.length || 0) > 0
    } catch (error) {
      console.error('Error checking active jobs:', error)
      return false
    }
  }

  /**
   * Search summary targets (students and staff) by name, email, or ID (exact match)
   * for single-user selection
   */
  static async searchSummaryTargets(query: string, limit: number = 10): Promise<Array<{
    id: string
    name: string
    email: string
    role: string
    school_id: string | null
    grade: string | null
    class: string | null
  }>> {
    try {
      if (!query || query.length < 2) {
        return []
      }

      const searchPattern = `%${query}%`

      // Check if query is a full UUID (exact match for ID search)
      const isFullUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(query)

      // If it's a full UUID, try exact ID match first
      if (isFullUuid) {
        const { data: exactMatch } = await supabase
          .from('users')
          .select('id, name, email, role, school_id, grade, class')
          .in('role', SUMMARY_TARGET_ROLES)
          .eq('id', query)
          .single()

        if (exactMatch) {
          return [exactMatch]
        }
      }

      // Search by name/email. Staff are outnumbered by students roughly 20:1, so a
      // single query would push them past the row limit for any common name
      // fragment. Search the two groups separately and list staff first, which
      // keeps them reachable without hiding the students admins usually look for.
      const searchByRoles = (roles: string[]) => supabase
        .from('users')
        .select('id, name, email, role, school_id, grade, class')
        .in('role', roles)
        .or(`name.ilike.${searchPattern},email.ilike.${searchPattern}`)
        .order('name')
        .limit(limit)

      const [staffResult, studentResult] = await Promise.all([
        searchByRoles(SUMMARY_TARGET_ROLES.filter(role => role !== 'student')),
        searchByRoles(['student'])
      ])

      if (staffResult.error || studentResult.error) {
        console.error('Error searching summary targets:', staffResult.error || studentResult.error)
        return []
      }

      return [...(staffResult.data || []), ...(studentResult.data || [])].slice(0, limit)
    } catch (error) {
      console.error('Error searching summary targets:', error)
      return []
    }
  }
}
